import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useApi } from '../state/ApiContext';

const TEAM_LOOKUP_ATTEMPTS = 3;
const TEAM_LOOKUP_RETRY_DELAY_MS = 100;

/**
 * Team-scoped agent pages are not an escape hatch around No Agents. Project
 * screens have their own effective policy and never pass through this route.
 */
const AgentPolicyBoundary: React.FC = () => {
  const { teamApi } = useApi();
  const location = useLocation();
  const teamId = Number(
    location.pathname.match(/\/console\/team\/(\d+)\/agent(?:\/|$)/)?.[1],
  );
  const [status, setStatus] = useState<
    'loading' | 'allowed' | 'blocked' | 'error'
  >('loading');

  useEffect(() => {
    let cancelled = false;
    if (!Number.isSafeInteger(teamId) || teamId <= 0) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    const checkPolicy = async () => {
      for (let attempt = 1; attempt <= TEAM_LOOKUP_ATTEMPTS; attempt += 1) {
        try {
          const response = await teamApi.apiTeamsGet();
          if (cancelled) return;
          const team = response.data.find(
            (candidate) => candidate.id === teamId,
          );
          if (team) {
            setStatus(
              (team as any).workingStyle === 'no_agents'
                ? 'blocked'
                : 'allowed',
            );
            return;
          }
        } catch {
          if (cancelled) return;
        }

        if (attempt < TEAM_LOOKUP_ATTEMPTS) {
          await new Promise((resolve) =>
            setTimeout(resolve, TEAM_LOOKUP_RETRY_DELAY_MS),
          );
          if (cancelled) return;
        }
      }
      setStatus('error');
    };
    void checkPolicy();
    return () => {
      cancelled = true;
    };
  }, [teamApi, teamId]);

  if (status === 'loading') {
    return <p className="descriptionText">Checking agent policy…</p>;
  }
  if (status === 'blocked') {
    return <Navigate to={`/console/team/${teamId}/agents`} replace />;
  }
  if (status === 'error') {
    return <p className="error-message">Agent pages are unavailable.</p>;
  }
  return <Outlet />;
};

export default AgentPolicyBoundary;
